import { Badge } from '@shared/components/ui/badge';
import { Search } from '@shared/components/ui/Search';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { formatDate } from '@shared/utils/FormatUtils';
import {
  collectKeysFromRows,
  humanizeColumnKey,
  isLikelyDateColumn,
  isLikelyMoneyColumn,
  isLikelyStatusMatchColumn,
  SETTLEMENT_EXCEPTION_COLUMN_PRIORITY,
  SETTLEMENT_LINE_COLUMN_PRIORITY,
  sortColumnKeys,
} from '../../lib/settlementTableKeys';
import { ChangeEvent, type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Variant = 'lines' | 'exceptions';

function toRecords(rows: unknown[]): Record<string, unknown>[] {
  return rows.filter((r): r is Record<string, unknown> => Boolean(r) && typeof r === 'object' && !Array.isArray(r));
}

function parseNumber(value: unknown): number | null {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value.replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function formatMoneyEs(value: number): string {
  try {
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(value);
  } catch {
    return String(value);
  }
}

function stringifyForSearch(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function rowMatchesQuery(row: Record<string, unknown>, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const tokens = q.split(/\s+/).filter(Boolean);
  const haystack = Object.values(row).map(stringifyForSearch).join(' ').toLowerCase();
  return tokens.every((t) => haystack.includes(t));
}

function matchTruth(value: unknown): boolean | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return value;
  const s = String(value).trim().toLowerCase();
  if (['1', 'true', 'yes', 'si', 'sí', 'matched', 'ok', 'conciliado'].includes(s)) return true;
  if (['0', 'false', 'no', 'unmatched', 'pending'].includes(s)) return false;
  return null;
}

interface FriendlySettlementTableProps {
  rows: unknown[];
  variant: Variant;
}

export function FriendlySettlementTable({ rows, variant }: FriendlySettlementTableProps) {
  const { t } = useTranslation('courierInvoicesModule');
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const normalized = useMemo(() => toRecords(rows), [rows]);

  const filtered = useMemo(
    () => normalized.filter((row) => rowMatchesQuery(row, searchQuery)),
    [normalized, searchQuery],
  );

  const columns = useMemo(() => {
    if (!normalized.length) return [];
    const priority = variant === 'exceptions' ? SETTLEMENT_EXCEPTION_COLUMN_PRIORITY : SETTLEMENT_LINE_COLUMN_PRIORITY;
    const q = searchQuery.trim();
    const sourceRows = q ? filtered : normalized;
    const keysSource = sourceRows.length > 0 ? sourceRows : normalized;
    return sortColumnKeys(collectKeysFromRows(keysSource), priority);
  }, [filtered, normalized, variant, searchQuery]);

  const columnLabel = (key: string) => t(`settlement.columns_labels.${key}`, { defaultValue: humanizeColumnKey(key) });

  const renderCell = (key: string, value: unknown): ReactNode => {
    if (value === null || value === undefined) {
      return <span className="text-muted-foreground">—</span>;
    }

    if (typeof value === 'boolean') {
      return (
        <Badge variant={value ? 'transparent-green' : 'outline'}>
          {value ? t('settlement.cell_yes') : t('settlement.cell_no')}
        </Badge>
      );
    }

    if (isLikelyStatusMatchColumn(key)) {
      const truth = matchTruth(value);
      if (truth !== null) {
        return (
          <Badge variant={truth ? 'transparent-green' : 'transparent-orange'}>
            {truth ? t('settlement.matched_yes') : t('settlement.matched_no')}
          </Badge>
        );
      }
    }

    const num = parseNumber(value);
    if (num !== null && isLikelyMoneyColumn(key)) {
      return <span className="tabular-nums text-foreground">{formatMoneyEs(num)} €</span>;
    }

    if (typeof value === 'number' && Number.isFinite(value) && /_id$|^id$/i.test(key)) {
      return <span className="tabular-nums text-foreground">{value}</span>;
    }

    if (typeof value === 'string' && isLikelyDateColumn(key)) {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) {
        return <span className="text-foreground">{formatDate(value)}</span>;
      }
    }

    if (typeof value === 'object') {
      const json = JSON.stringify(value);
      return (
        <span className="font-mono text-xs text-muted-foreground max-w-[min(18rem,28vw)] truncate block" title={json}>
          {json}
        </span>
      );
    }

    const str = String(value);
    const isLong = str.length > 48;
    const display = isLong ? `${str.slice(0, 45)}…` : str;
    return (
      <span className="text-foreground text-sm max-w-[min(14rem,22vw)] truncate inline-block align-bottom" title={str}>
        {display}
      </span>
    );
  };

  if (!normalized.length) {
    return <p className="text-sm text-muted-foreground py-8 text-center">{t('settlement.empty_rows')}</p>;
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  return (
    <div className="space-y-3">
      <Search
        placeholder={t('settlement.table_search_placeholder')}
        value={inputValue}
        onChange={handleInputChange}
        onEnter={(value) => setSearchQuery((value ?? inputValue).trim())}
        className="w-full max-w-sm"
        dataTestId={`settlement-table-search-${variant}`}
        inputDataTestId={`settlement-table-search-input-${variant}`}
      />

      {searchQuery && filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">{t('settlement.table_no_filter_matches')}</p>
      ) : (
        <div className="rounded-lg border border-card-border shadow-sm overflow-auto max-h-[calc(100vh-320px)]">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-card-border bg-muted/95">
                {columns.map((key) => (
                  <TableHead
                    key={key}
                    className="sticky top-0 z-[1] bg-muted/95 backdrop-blur-sm text-foreground font-medium whitespace-nowrap text-xs uppercase tracking-wide text-subtext shadow-[0_1px_0_0_hsl(var(--border))]"
                  >
                    {columnLabel(key)}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((row, idx) => (
                <TableRow key={idx} className="border-card-border hover:bg-muted/40 transition-colors even:bg-muted/15">
                  {columns.map((key) => (
                    <TableCell key={key} className="align-middle py-2.5 text-sm border-card-border">
                      {renderCell(key, row[key])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
